import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue, useRecoilState } from 'recoil'
import {
  Panel,
  Flex,
  FlexItem,
  FormElement,
  Text,
  TEXT_VARIANT_BOLD,
  InputText,
  INPUT_TEXT_VARIANT_DANGER,
  Button,
  BUTTON_VARIANT_NEUTRAL,
  TrustedEmailInput,
  HelpIcon,
  IconLock
} from 'mailup-web-components'

import { isNameInputValidState, newSenderState } from '../recoil/state/sender'
import useTrustedEmailSender from 'Hooks/useTrustedEmailSender'
import { updateSenderCallback } from '../recoil/callbacks/callbacks'
import { pecPlusConfigState } from '../recoil/state/page'
import { evalCondition } from 'mailup-react-helpers'

const SenderBox = () => {
  const intl = useIntl()

  const isWhiteLabel = evalCondition('co:WHITELABEL')

  const isNameInputValid = useRecoilValue(isNameInputValidState)
  const [isDisabled, setBtnDisabled] = useState(false)
  const [{ email, name }, setSender] = useRecoilState(newSenderState)
  const [isDirty, setIsDirty] = useState({ email: false, name: false })
  const { isListPecPlus } = useRecoilValue(pecPlusConfigState)

  const updateSender = useRecoilCallback(updateSenderCallback, [])

  const trustedSenderProps = useTrustedEmailSender('trustedSender', email, true)

  const onChangeName = e => {
    setIsDirty(prev => ({ ...prev, name: true }))
    const newName = e.target.value
    setSender(prev => ({ ...prev, name: newName }))
  }

  useEffect(() => {
    setSender(prev => ({ ...prev, email: trustedSenderProps.value }))
  }, [trustedSenderProps.value])

  useEffect(() => {
    setBtnDisabled(
      !isNameInputValid ||
        (!isListPecPlus &&
          trustedSenderProps.variant !== 'success' &&
          trustedSenderProps.variant !== 'warning' &&
          trustedSenderProps.variant !== 'neutral')
    )
  }, [trustedSenderProps.variant, isNameInputValid])

  const getFeedbackVariant = () => {
    if (isDirty.name && !isNameInputValid) {
      return INPUT_TEXT_VARIANT_DANGER
    }
  }

  const getFeedbackString = () => {
    if (isDirty.name && !isNameInputValid) {
      return intl.formatMessage({
        id: 'Load.MailupCommon.Required'
      })
    }
  }

  const pecTooltip = isWhiteLabel
    ? intl.formatMessage({
        id: 'Load.PecPlus.senderPecTooltipWL'
      })
    : intl.formatMessage(
        { id: 'Load.PecPlus.senderPecTooltip' },
        { values: { strong: chunks => <strong>{chunks}</strong> } }
      )

  return (
    <Panel>
      <Flex width='100%'>
        <FlexItem width='100%' margin='0 40px 0 0'>
          <Flex width='100%'>
            <Text
              variant={TEXT_VARIANT_BOLD}
              className='sender-label'
              htmlElement='span'
            >
              {intl.formatMessage({ id: 'Load.Queue.SenderName' })}
              {' *'}
            </Text>

            <FlexItem width='60%' margin='0 0 0 24px'>
              <FormElement
                feedback={getFeedbackString()}
                feedbackVariant={getFeedbackVariant()}
              >
                <InputText
                  value={name}
                  disabled={evalCondition('uo:EMAILSENDERLOCKED')}
                  onChange={onChangeName}
                  variant={getFeedbackVariant()}
                  data-mu-test='email-sending-queue-manage__sender-name'
                />
              </FormElement>
            </FlexItem>
          </Flex>
        </FlexItem>

        <FlexItem width='100%' margin='0 40px 0 0'>
          <Flex width='100%'>
            <Text
              variant={TEXT_VARIANT_BOLD}
              className='sender-label'
              htmlElement='span'
            >
              {intl.formatMessage({ id: 'Load.Queue.SenderEmail' })}
              {' *'}
              {isListPecPlus && (
                <HelpIcon
                  text={intl.formatMessage({
                    id: 'Load.PecPlus.AltImgMailMittPec'
                  })}
                />
              )}
            </Text>

            <FlexItem width='60%' margin='0 0 0 24px'>
              {isListPecPlus ? (
                <span className='input-container' data-tip={pecTooltip}>
                  <IconLock className='icon' />
                  <InputText value={email} disabled />
                </span>
              ) : (
                <TrustedEmailInput
                  id='trustedSender'
                  {...trustedSenderProps}
                  disabled={evalCondition('uo:EMAILSENDERLOCKED')}
                  dataAttributeTest='email-sending-queue-manage__sender-email'
                  data-mu-test='email-sending-queue-manage__sender-email'
                />
              )}
            </FlexItem>
          </Flex>
        </FlexItem>

        <Button
          kind={BUTTON_VARIANT_NEUTRAL}
          onClick={updateSender}
          disabled={isDisabled}
          data-mu-test='email-sending-queue-manage__update-sender'
        >
          {intl.formatMessage({ id: 'Load.MailupCommon.Update' })}
        </Button>
      </Flex>
    </Panel>
  )
}

export default SenderBox
